import get from 'lodash-es/get';
import round from 'lodash-es/round';

import { ROUND_PRECISION, STATUSES } from '@/constants';
import { Money } from '@/models/money';
import { Product, UnitDenomination } from '@/models/product';
import i18n from '@/plugins/i18n';
import { decimal, money } from '@/utils/i18n';

/**
 * Gets lowest non-zero number, or 0 if both numbers are 0
 * @param {number} firstPrice to be compared
 * @param {number} secondPrice to be compared
 * @returns {number} A number representing the final value
 */
export function getLowestPrice(firstPrice: number, secondPrice: number): number {
  if (firstPrice === 0 && secondPrice === 0) {
    return 0;
  }

  return Math.min.apply(null, [firstPrice, secondPrice].filter(Number));
}

/**
 * Calculates an accumulated value of an array of objects, resulting in one final value
 * @param {Array} items to loop through
 * @param {string} field to search for, can be nested, e.g.: 'wholesale.amount'
 * @returns {number|null} A number representing the final value or null
 */
export function calculateTotal<T = Record<string, unknown>>(items: T[], field: string) {
  const total = items.map((item) => get(item, field.split('.'))).reduce((acc, total) => acc + total, 0);

  return typeof total === 'number' ? round(total, ROUND_PRECISION.CURRENCY) : null;
}

/**
 * Returns a price per unit, given a price per case
 * @param {number} casePrice the price per case
 * @param {number} unitsPerCase the number of units in a case
 * @param {number} precision how many decimals to round
 * @returns {number} price per unit
 */
export function getUnitPrice(
  casePrice: number,
  unitsPerCase: number,
  precision: number = ROUND_PRECISION.CURRENCY,
): number {
  return round(casePrice / unitsPerCase, precision);
}

/**
 * Returns quantity of cases, given a total unit quantity
 * @param {number} unitQuantity the total number of units
 * @param {number} unitsPerCase the number of units in a case
 * @param {number} precision how many decimals to round
 * @returns {number} number of cases
 */
export function getCaseQuantity(
  unitQuantity: number,
  unitsPerCase = 1,
  precision: number = ROUND_PRECISION.INVENTORY,
): number {
  return round(unitQuantity / unitsPerCase, precision);
}

/**
 * Returns total quantity of units, given a quantity of cases
 * @param {number} caseQuantity the number of cases
 * @param {number} unitsPerCase the number of units in a case
 * @param {number} precision how many decimals to round
 * @returns {number} total number of units
 */
export function getUnitQuantity(
  caseQuantity: number,
  unitsPerCase = 1,
  precision: number = ROUND_PRECISION.INVENTORY,
): number {
  return round(caseQuantity * unitsPerCase, precision);
}

/**
 * Parses an order lineItem to return fields for displaying the quantity in the UI
 */
export function getQuantityDisplay(args: {
  quantity: number;
  soldInBulk?: boolean;
  bulkUnits: number;
  unitsPerCase?: number;
  unitOfMeasure: string;
  unitDenomination: UnitDenomination;
}) {
  const { quantity, soldInBulk = false, bulkUnits, unitsPerCase = 1, unitOfMeasure, unitDenomination } = args;

  let unitOfMeasureString = getUnitLabel({ unitOfMeasure, unitDenomination });

  // NOTE: Not compatible with all languages, but it works for our current USA-centric Units of Measure
  if (+quantity !== 1) {
    unitOfMeasureString += 's';
  }

  let soldBy = `${decimal(quantity)} ${unitOfMeasureString}`;
  let units;

  if (soldInBulk && unitsPerCase > 1) {
    const caseString = bulkUnits > 1 ? 'common.cases' : 'common.case';

    units = `${soldBy}`;
    soldBy = `${bulkUnits} ${i18n.t(caseString)}`;
  }

  return {
    soldBy,
    units,
  };
}

/**
 * For rendering a price range given a collection of varieties, and the type of price
 */
interface VarietyPriceProduct {
  baseUnitPrice?: Money | null;
  children?: VarietyPriceProduct[] | null;
  effectivePrice: Money;
  pricePerUnit: Money;
}

/**
 * For rendering a price range given a collection of varieties and the type of price.
 */
export const getVarietyPriceRange = (
  field: keyof Omit<VarietyPriceProduct, 'children'>,
  product: VarietyPriceProduct,
) => {
  const { children } = product;

  if (!children) {
    return '';
  }

  const prices = children.map((i) => i[field]?.amount ?? 0);

  const priceRange = {
    max: {
      ...children[0][field],
      amount: Math.max(...prices),
    },
    min: {
      ...children[0][field],
      amount: Math.min(...prices),
    },
  };

  let priceString = money(priceRange.min);

  if (priceRange.max.amount !== priceRange.min.amount) {
    priceString += ` - ${money(priceRange.max)}`;
  }

  return priceString;
};

/**
 * @param {object} product contains the product data
 * @param {boolean} [isAbbreviated] whether to display the UOM abbreviation when unit demonination is not single
 * @param {object} [product.unitDenomination] contains unit denomination data for the product
 * @param {number} [product.unitDenomination.id] the unit denomination id
 * @param {string} [product.unitDenomination.label] the unit denomination label
 * @param {string} product.unitOfMeasure the unit of measure
 */
export function getUnitLabel(
  product: {
    unitDenomination: UnitDenomination;
    unitOfMeasure: string;
    unitOfMeasureAbbreviation?: string;
  },
  isAbbreviated = false,
): string {
  const {
    unitDenomination = { ...STATUSES.PRODUCT_UNIT_DENOMINATION.ONE },
    unitOfMeasure,
    unitOfMeasureAbbreviation,
  } = product;

  // TODO: support conversion case for PoST, should be removed later
  if (unitOfMeasure.includes(unitDenomination.label)) {
    return unitOfMeasure;
  }

  if (unitDenomination.id !== STATUSES.PRODUCT_UNIT_DENOMINATION.ONE.id) {
    return `${unitDenomination.label} ${isAbbreviated ? unitOfMeasureAbbreviation : unitOfMeasure}`;
  }

  return unitOfMeasure;
}

/**
 * @param {object} product contains the product data
 * @param {boolean} [product.soldByCase] whether to display "Case" instead
 * @param {boolean} [product.shouldShowInCases] same use as product.soldByCase
 * @returns {string} the unit of measure optionally prefixed with the unit denomination label
 */
export function getSoldBy(product: Product): string {
  const { soldByCase, shouldShowInCases } = product;

  if (soldByCase || shouldShowInCases) {
    return i18n.t('common.case');
  }

  return getUnitLabel(product);
}

/**
 * Calculates the available inventory hint text below the quantity input
 * @param {number} [product.unitMultiplier] number of units per case
 * @param {number} [product.availableInventory] availability of the product
 * @param {number} [product.inventoryManagement] product's management type
 * @param {boolean} [product.unitOfMeasure] product's unit of measurement
 * @param {boolean} isSoldByCase is the product selected to be sold by case
 */
export function availableInventoryHint(product: Product, isSoldByCase: boolean): string {
  const { unitMultiplier, availableInventory, inventoryManagement } = product;

  const inventoryMessaage = isSoldByCase
    ? `${getCaseQuantity(availableInventory, unitMultiplier)} ${i18n.t('common.cases')}`
    : availableInventory;
  const quantity = inventoryManagement === STATUSES.INVENTORY_MANAGEMENT.UNLIMITED ? '∞' : inventoryMessaage;
  const message = i18n.t('common.num_available', { num: quantity });

  return isSoldByCase ? message : `${message} (${getUnitLabel(product)})`;
}
